// Trackeamento > Eventos e conversões: IDs do Pixel/Google, eventos automáticos e o que a tag recebeu.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, MessageCircle, MousePointerClick, Phone, Plug, RefreshCw, Send, Server } from 'lucide-react'
import { TRACK_EVENT_LABELS, type TrackEvent, type TrackingSetupDto, type TrackingStatsDto } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { Empty, ErrorState, Loading, PageHead, Platform, Switch, Tabs } from '../../../ui'
import { useToast } from '../../../ui/toast'
import { useTracking, useTrackingSources, useTrackingStats, useUpdateTracking } from '../../../features/tracking/api'
import { CopyBox } from '../../../features/tracking/components'
import '../../../features/account/pages.css'

export default function TrackingEvents() {
  const company = useCompany()
  const q = useTracking(company.id)
  if (q.isLoading) return <Loading lines={6} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  return <Events setup={q.data!} />
}

function Events({ setup }: { setup: TrackingSetupDto }) {
  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Eventos e conversões" sub="Diga ao Seu Impulso qual é o seu Pixel da Meta (código que avisa o Instagram e o Facebook de quem virou contato) e a sua tag do Google. A tag avisa as duas plataformas sempre que alguém vira contato." />
      <ConnectedAccountsCard setup={setup} />
      <div className="grid g-2 align-start">
        <MetaCard setup={setup} />
        <GoogleCard setup={setup} />
      </div>
      <AutoEventsCard setup={setup} />
      <ServerSideCard setup={setup} />
      <StatsCard />
      <details className="card">
        <summary className="strong" style={{ cursor: 'pointer' }}>Avançado: registrar vendas feitas no site</summary>
        <div className="stack mt-12" style={{ gap: 10 }}>
          <p className="small muted">Se o seu site tem página de "compra concluída", peça para quem cuida do site colocar este código nela (com o valor da venda). A venda vai para a Meta, para o Google e para os números abaixo.</p>
          <CopyBox label="Código de compra" value={'<script>\n  window.impulso && window.impulso.track(\'purchase\', { value: 199.90 })\n</script>'} />
        </div>
      </details>
    </div>
  )
}

function useSaver() {
  const company = useCompany()
  const update = useUpdateTracking(company.id)
  const toast = useToast()
  return { update, save: (body: Parameters<typeof update.mutate>[0], msg = 'Salvo.') => update.mutate(body, { onSuccess: () => toast(msg), onError: (e) => toast(errorMessage(e)) }) }
}

function MetaCard({ setup }: { setup: TrackingSetupDto }) {
  const canEdit = useCan('company.edit_settings')
  const { update, save } = useSaver()
  const [pixel, setPixel] = useState(setup.settings.metaPixelId ?? '')
  useEffect(() => setPixel(setup.settings.metaPixelId ?? ''), [setup.settings.metaPixelId])
  const detected = setup.lastCheck?.ids.metaPixelIds ?? []
  return (
    <form className="card stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); save({ settings: { metaPixelId: pixel.replace(/\D/g, '') || null } }) }}>
      <div className="row" style={{ gap: 10 }}><span className="pf-logo pf-meta lg">M</span><div><h3>Pixel da Meta</h3><div className="small muted">Instagram e Facebook</div></div>{setup.settings.metaPixelId && <span className="badge green" style={{ marginLeft: 'auto' }}>Ligado</span>}</div>
      <div className="field">
        <label htmlFor="px">ID do pixel</label>
        <input id="px" className="input" inputMode="numeric" placeholder="Ex.: 123456789012345" value={pixel} onChange={(e) => setPixel(e.target.value)} disabled={!canEdit} />
      </div>
      {detected.length > 0 && !setup.settings.metaPixelId && <div className="small">Encontramos no seu site: {detected.map((id) => <button key={id} type="button" className="chip" style={{ padding: '2px 8px', marginLeft: 4 }} onClick={() => setPixel(id)}>{id}</button>)}</div>}
      <details className="small">
        <summary className="strong" style={{ cursor: 'pointer', color: 'var(--primary)' }}>Onde encontro?</summary>
        <ol className="steps-list mt-12">
          <li><span>Entre em business.facebook.com e abra o <strong>Gerenciador de Eventos</strong>.</span></li>
          <li><span>Em "Fontes de dados", clique no seu pixel. Se não tiver, clique em "Conectar fonte de dados" → Web.</span></li>
          <li><span>O ID aparece embaixo do nome, só com números. Copie e cole aqui.</span></li>
        </ol>
      </details>
      {canEdit && <div className="form-actions"><button className="btn primary" disabled={update.isPending || pixel.replace(/\D/g, '') === (setup.settings.metaPixelId ?? '')}>Salvar</button></div>}
    </form>
  )
}

function GoogleCard({ setup }: { setup: TrackingSetupDto }) {
  const canEdit = useCan('company.edit_settings')
  const { update, save } = useSaver()
  const [tag, setTag] = useState(setup.settings.googleTagId ?? '')
  const [label, setLabel] = useState(setup.settings.googleConversionLabel ?? '')
  const saved = setup.settings.googleConversionLabels ?? {}
  const [per, setPer] = useState({ lead: saved.lead ?? '', contact: saved.contact ?? '', purchase: saved.purchase ?? '' })
  useEffect(() => { setTag(setup.settings.googleTagId ?? ''); setLabel(setup.settings.googleConversionLabel ?? ''); setPer({ lead: saved.lead ?? '', contact: saved.contact ?? '', purchase: saved.purchase ?? '' }) }, [setup.settings.googleTagId, setup.settings.googleConversionLabel, saved.lead, saved.contact, saved.purchase])
  const changed = tag.trim() !== (setup.settings.googleTagId ?? '') || label.trim() !== (setup.settings.googleConversionLabel ?? '')
    || (['lead', 'contact', 'purchase'] as const).some((k) => per[k].trim() !== (saved[k] ?? ''))
  return (
    <form className="card stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); save({ settings: { googleTagId: tag.trim() || null, googleConversionLabel: label.trim() || null, googleConversionLabels: { lead: per.lead.trim() || null, contact: per.contact.trim() || null, purchase: per.purchase.trim() || null } } }) }}>
      <div className="row" style={{ gap: 10 }}><span className="pf-logo pf-google lg">G</span><div><h3>Tag do Google</h3><div className="small muted">Google Ads e Analytics</div></div>{setup.settings.googleTagId && <span className="badge green" style={{ marginLeft: 'auto' }}>Ligada</span>}</div>
      <div className="grid g-2" style={{ gap: 10 }}>
        <div className="field">
          <label htmlFor="gt">ID da tag</label>
          <input id="gt" className="input" placeholder="AW-123456789" value={tag} onChange={(e) => setTag(e.target.value)} disabled={!canEdit} />
        </div>
        <div className="field">
          <label htmlFor="gl">Rótulo da conversão</label>
          <input id="gl" className="input" placeholder="Opcional" value={label} onChange={(e) => setLabel(e.target.value)} disabled={!canEdit} />
        </div>
      </div>
      <details className="small">
        <summary className="strong" style={{ cursor: 'pointer', color: 'var(--primary)' }}>Um rótulo para cada tipo de conversão (opcional)</summary>
        <p className="muted mt-8">Se você criou conversões diferentes no Google Ads, informe o rótulo de cada uma. Onde ficar em branco, vale o rótulo geral acima.</p>
        <div className="grid g-3" style={{ gap: 10 }}>
          {([['lead', 'Formulário enviado'], ['contact', 'WhatsApp ou ligação'], ['purchase', 'Compra']] as const).map(([k, name]) => (
            <div className="field" key={k}><label htmlFor={`gl-${k}`}>{name}</label><input id={`gl-${k}`} className="input" value={per[k]} onChange={(e) => setPer({ ...per, [k]: e.target.value })} disabled={!canEdit} /></div>
          ))}
        </div>
      </details>
      <span className="small muted">Dica: pode colar o "AW-…/…" inteiro no primeiro campo que a gente separa.</span>
      <details className="small">
        <summary className="strong" style={{ cursor: 'pointer', color: 'var(--primary)' }}>Onde encontro?</summary>
        <ol className="steps-list mt-12">
          <li><span>No Google Ads, vá em <strong>Metas → Conversões → Resumo</strong> e clique em "+ Nova ação de conversão" → Site.</span></li>
          <li><span>Escolha configurar o código manualmente. Na tela da tag aparece algo como <code>send_to: 'AW-123456789/AbCdEf'</code>.</span></li>
          <li><span>Copie esse valor (AW-…/…) e cole aqui. Só usa Analytics? Cole o ID que começa com G-.</span></li>
        </ol>
      </details>
      {canEdit && <div className="form-actions"><button className="btn primary" disabled={update.isPending || !changed}>Salvar</button></div>}
    </form>
  )
}

function AutoEventsCard({ setup }: { setup: TrackingSetupDto }) {
  const canEdit = useCan('company.edit_settings')
  const { update, save } = useSaver()
  const a = setup.settings.autoEvents
  const rows = [
    { key: 'whatsapp' as const, icon: MessageCircle, title: 'Cliques no WhatsApp', text: 'Qualquer botão ou link que abre o WhatsApp vira conversão "Contato".' },
    { key: 'phone' as const, icon: Phone, title: 'Cliques para ligar', text: 'Links de telefone (tel:) no celular.' },
    { key: 'forms' as const, icon: Send, title: 'Formulários enviados', text: 'Envio de formulários de contato vira conversão "Lead". Formulários de login são ignorados.' },
  ]
  return (
    <div className="card">
      <div className="row" style={{ gap: 10 }}><MousePointerClick size={20} color="var(--primary)" /><h2>O que conta como contato</h2></div>
      <p className="small muted mt-8">Ligados por padrão. Valem para o seu site e para as páginas feitas aqui.</p>
      <div className="list mt-12">
        {rows.map((r) => (
          <div key={r.key} className="list-item" style={{ alignItems: 'center' }}>
            <span className="ic purple"><r.icon size={17} /></span>
            <div style={{ flex: 1 }}><div className="strong">{r.title}</div><div className="small muted">{r.text}</div></div>
            <Switch on={a[r.key]} disabled={!canEdit || update.isPending} label={r.title} onChange={(v) => save({ settings: { autoEvents: { [r.key]: v } } }, v ? `${r.title}: ligado.` : `${r.title}: desligado.`)} />
          </div>
        ))}
      </div>
    </div>
  )
}

const CONV: TrackEvent[] = ['whatsapp_click', 'phone_click', 'form_submit', 'lead', 'purchase']

function StatsCard() {
  const company = useCompany()
  const [days, setDays] = useState<'7' | '30' | '90'>('30')
  const q = useTrackingStats(company.id, Number(days))
  return (
    <div className="card stack" style={{ gap: 16 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <h2>O que a tag recebeu</h2>
        <Tabs value={days} onChange={setDays} options={[{ value: '7', label: '7 dias' }, { value: '30', label: '30 dias' }, { value: '90', label: '90 dias' }]} />
      </div>
      {q.isLoading ? <Loading /> : q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <StatsBody s={q.data!} />}
    </div>
  )
}

function StatsBody({ s }: { s: TrackingStatsDto }) {
  const total = Object.values(s.totals).reduce((a, b) => a + b, 0)
  if (total === 0) return <Empty title="Nenhum sinal recebido ainda">Assim que alguém visitar o seu site ou página com a tag instalada, os números aparecem aqui.</Empty>
  const convOf = (d: TrackingStatsDto['byDay'][number]) => CONV.reduce((a, e) => a + d[e], 0)
  const max = Math.max(1, ...s.byDay.map((d) => Math.max(d.page_view, convOf(d))))
  return (
    <>
      <div className="grid g-3" style={{ gap: 12 }}>
        {(['page_view', ...CONV] as TrackEvent[]).filter((e) => e === 'page_view' || s.totals[e] > 0 || e === 'whatsapp_click').map((e) => (
          <div key={e} className="kpi" style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 12 }}>
            <div className="label">{TRACK_EVENT_LABELS[e]}</div>
            <div className="value" style={{ fontSize: 22 }}>{s.totals[e].toLocaleString('pt-BR')}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="bars" role="img" aria-label="Visitas e contatos por dia">
          {s.byDay.map((d) => {
            const conv = convOf(d)
            return (
              <div key={d.day} className="bar" data-tip={`${new Date(`${d.day}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}: ${d.page_view} visitas, ${conv} contatos`}>
                <span className="v" style={{ height: `${(Math.max(0, d.page_view - conv) / max) * 100}%` }} />
                <span className="c" style={{ height: `${(conv / max) * 100}%` }} />
              </div>
            )
          })}
        </div>
        <div className="legend mt-8"><span><i style={{ background: 'var(--primary-100)' }} />Visitas</span><span><i style={{ background: 'var(--primary)' }} />Contatos</span></div>
      </div>
      {s.bySource.length > 0 && (
        <div className="table-wrap">
          <table className="table stack-sm">
            <thead><tr><th>Origem</th><th className="right">Visitas</th><th className="right">Contatos</th><th className="right">Taxa</th></tr></thead>
            <tbody>
              {s.bySource.map((r) => (
                <tr key={r.source}><td>{r.label}</td><td className="right" data-label="Visitas">{r.pageViews}</td><td className="right" data-label="Contatos">{r.conversions}</td><td className="right" data-label="Taxa">{r.pageViews ? `${((r.conversions / r.pageViews) * 100).toFixed(1).replace('.', ',')}%` : '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** Busca pixels e ações de conversão nas contas conectadas e aplica com um clique. */
function ConnectedAccountsCard({ setup }: { setup: TrackingSetupDto }) {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const { update, save } = useSaver()
  const [asked, setAsked] = useState(false)
  const q = useTrackingSources(company.id, asked)
  const s = setup.settings
  const items = q.data?.items ?? []
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 10 }}><Plug size={20} color="var(--primary)" /><h2>Pegar das contas conectadas</h2></div>
        <button className="btn sm" disabled={q.isFetching} onClick={() => (asked ? void q.refetch() : setAsked(true))}>
          <RefreshCw size={14} className={q.isFetching ? 'spin' : ''} /> {asked ? 'Buscar de novo' : 'Buscar nas minhas contas'}
        </button>
      </div>
      <p className="small muted">Em vez de procurar os códigos, a gente consulta o Google Ads e a Meta conectados e você escolhe com um clique.</p>
      {q.error && <div className="form-error">{(q.error as Error).message}</div>}
      {asked && !q.isFetching && !q.error && items.length === 0 && (
        <div className="callout small"><span>Nenhuma conta de anúncios conectada a esta empresa. <Link className="strong" to={`/app/${company.id}/contas`}>Conectar Google ou Meta</Link></span></div>
      )}
      {items.map((it) => (
        <div key={it.connectionId} className="sub-card stack" style={{ gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
          <Platform platform={it.provider} label={`${it.accountName ?? ''}`.trim() || undefined} />
          {it.error && <div className="small" style={{ color: 'var(--red)' }}>{it.error}</div>}
          {it.pixels.map((p) => {
            const inUse = s.metaPixelId === p.id && (!s.serverSide?.metaConnectionId || s.serverSide.metaConnectionId === it.connectionId)
            return (
              <div key={p.id} className="row between wrap" style={{ gap: 8 }}>
                <span className="small"><b>Pixel</b> {p.name} <span className="muted">· {p.id}{p.lastFiredAt ? ` · último sinal ${new Date(p.lastFiredAt).toLocaleDateString('pt-BR')}` : ' · nunca recebeu sinais'}</span></span>
                {inUse ? <span className="badge green"><CheckCircle2 size={12} /> Em uso</span>
                  : canEdit && <button className="btn sm" disabled={update.isPending} onClick={() => save({ settings: { metaPixelId: p.id, serverSide: { metaConnectionId: it.connectionId } } }, 'Pixel da Meta ligado.')}>Usar este pixel</button>}
              </div>
            )
          })}
          {it.conversionActions.filter((a) => a.kind === 'webpage' && a.tagId).map((a) => {
            const inUse = s.googleTagId === a.tagId && s.googleConversionLabel === a.label
            return (
              <div key={a.id} className="row between wrap" style={{ gap: 8 }}>
                <span className="small"><b>Conversão no site</b> {a.name} <span className="muted">· {a.tagId}/{a.label}</span></span>
                {inUse ? <span className="badge green"><CheckCircle2 size={12} /> Em uso</span>
                  : canEdit && <button className="btn sm" disabled={update.isPending} onClick={() => save({ settings: { googleTagId: a.tagId, googleConversionLabel: a.label } }, 'Conversão do Google ligada.')}>Usar esta conversão</button>}
              </div>
            )
          })}
          {it.conversionActions.filter((a) => a.kind === 'upload').map((a) => {
            const inUse = s.serverSide?.googleUploadActionId === a.id && s.serverSide?.googleConnectionId === it.connectionId
            return (
              <div key={a.id} className="row between wrap" style={{ gap: 8 }}>
                <span className="small"><b>Importação de contatos</b> {a.name}</span>
                {inUse ? <span className="badge green"><CheckCircle2 size={12} /> Em uso</span>
                  : canEdit && <button className="btn sm" disabled={update.isPending} onClick={() => save({ settings: { serverSide: { googleConnectionId: it.connectionId, googleUploadActionId: a.id } } }, 'Os contatos vão ser enviados ao Google pelo servidor.')}>Enviar contatos para esta</button>}
              </div>
            )
          })}
          {!it.error && it.pixels.length === 0 && it.conversionActions.length === 0 && <span className="small muted">Nenhum pixel ou conversão encontrado nesta conta.</span>}
        </div>
      ))}
    </div>
  )
}

/** Envio dos contatos das landing pages pelo servidor (mais preciso que só o navegador). */
function ServerSideCard({ setup }: { setup: TrackingSetupDto }) {
  const canEdit = useCan('company.edit_settings')
  const { update, save } = useSaver()
  const ss = setup.settings.serverSide
  const metaOn = !!(ss?.metaConnectionId && setup.settings.metaPixelId)
  const googleOn = !!(ss?.googleConnectionId && ss.googleUploadActionId)
  return (
    <div className="card">
      <div className="row" style={{ gap: 10 }}><Server size={20} color="var(--primary)" /><h2>Envio pelo servidor</h2><span className="badge purple">Recomendado</span></div>
      <p className="small muted mt-8">Bloqueadores de anúncio e o iPhone escondem parte das conversões do navegador. Com o envio pelo servidor, cada contato das suas páginas também vai direto para a Meta (API de Conversões) e para o Google (importação), sem contar em dobro.</p>
      <div className="list mt-12">
        <div className="list-item" style={{ alignItems: 'center' }}>
          <span className="pf-logo pf-meta">M</span>
          <div style={{ flex: 1 }}>
            <div className="strong">Meta: API de Conversões</div>
            <div className="small muted">{metaOn ? `Ligado no pixel ${setup.settings.metaPixelId}.` : 'Escolha um pixel em "Pegar das contas conectadas" para ligar.'}</div>
          </div>
          <Switch on={metaOn} disabled={!canEdit || update.isPending || !metaOn} label="API de Conversões da Meta" onChange={() => save({ settings: { serverSide: { metaConnectionId: null } } }, 'Envio para a Meta desligado.')} />
        </div>
        <div className="list-item" style={{ alignItems: 'center' }}>
          <span className="pf-logo pf-google">G</span>
          <div style={{ flex: 1 }}>
            <div className="strong">Google: importação de contatos</div>
            <div className="small muted">{googleOn ? 'Ligado. Use uma ação de conversão de importação separada da conversão do site.' : 'Escolha uma ação do tipo "importação" em "Pegar das contas conectadas".'}</div>
          </div>
          <Switch on={googleOn} disabled={!canEdit || update.isPending || !googleOn} label="Importação de contatos no Google" onChange={() => save({ settings: { serverSide: { googleConnectionId: null, googleUploadActionId: null } } }, 'Envio para o Google desligado.')} />
        </div>
      </div>
    </div>
  )
}
